import {
  EMAIL,
  SCHEDULE,
  TASK,
  EmailInitialContent,
  ScheduleInitialContent,
  TaskInitialContent,
  InteractionType,
  WindowVariant,
} from './ActivityCreatorTypes';
import { CrmSearchFilter, CrmSearchFilterGroup } from './CrmSearchFilter';
import { CrmSearchSort } from './CrmSearchSort';
import { GoogleDriveIntegrationEvent } from './GoogleDriveIntegration';
import { UpdateAssociationModalState } from './UpdateAssociationModalStates';

// helper types
type Nullable<T> = T | null | undefined;
export type NullableOfType<T> = {
  [P in keyof T]: Nullable<T[P]>;
};

// Looser typing of https://github.com/HubSpotEngineering/crm-events-viz/blob/master/crm-events-viz-core/static/js/types/TimelineEvent.ts#L14
// TODO: Ensure strict typing after the rollout of CRM:Events:EngagementDetailMigration
type EngagementProperty<TValue extends string = string> = {
  isEncrypted: boolean;
  sensitivityLevel: string;
  value: TValue;
};

type EngagementObject = {
  activityType?: string;
  bodyPreview?: string;
  bodyPreviewHtml?: string;
  bodyPreviewIsTruncated: boolean;
  createdBy?: number;
  id: number;
  ownerId?: number;
  portalId?: number;
  queueMembershipIds: number[];
  source: string;
  timestamp: number;
  type: string;
  hasInlineImagesStripped?: boolean;
  strippedAttachmentCount?: number;
  uid?: string;
  threadId?: string;
};

type EngagementDataBase = {
  attachments: object[];
  engagementType?: object;
  objectTypeId?: string;
  objectId?: number;
  currentUserPermissions?: object;
};

type EngagementData<T extends object> = EngagementDataBase &
  (
    | {
        engagement: EngagementObject;
        metadata: T;
        properties?: Record<string, EngagementProperty>;
      }
    | {
        engagement?: EngagementObject;
        metadata?: T;
        properties: Record<string, EngagementProperty>;
      }
  );

type TimelineEventData = EngagementData<object>;

export type DispatchData = {
  [k in string]: unknown;
};

export type DispatchPayload = {
  actionType: string;
  data: DispatchData;
};

interface OpenUpdateAssociationModalData {
  currentState: UpdateAssociationModalState;
  fromObjectId: number;
  fromObjectTypeId: string;
  toObjectId: number;
  toObjectTypeId: string;
}

interface OpenReplaceBillingContactModalData {
  contractId: number;
  currentBillingContactId: number;
}

type OpenPropertyDocumentationData = {
  propertyName: string;
  objectTypeId: string;
};

const HUBSPOT_DEFINED = 'HUBSPOT_DEFINED';
const USER_DEFINED = 'USER_DEFINED';
const INTEGRATOR_DEFINED = 'INTEGRATOR_DEFINED';

type AssociationCategory =
  | typeof HUBSPOT_DEFINED
  | typeof USER_DEFINED
  | typeof INTEGRATOR_DEFINED;

type AdditionalDefaultAssociation = {
  associationTypeId: number;
  associationCategory: AssociationCategory;
  toObjectId: string;
  toObjectTypeId: string;
};

interface OpenObjectBuilderData {
  /**
   * The object type ID to build or add an association of. "Add existing" or "Create new" of this type
   */
  objectTypeId: string;
  associationTypeId?: number;
  associationCategory?: string;
  /**
   * If we are opening to create an association to another object, this is the starting information for the association
   */
  association?: {
    /**
     * The object type ID of the association.  The one we are opening the ObjectBuilder from
     */
    objectTypeId: string;
    /**
     * The object ID of the association. The one we are opening the ObjectBuilder from.
     */
    objectId: number;
    associationTypeId: number;
    associationCategory: string;
  };
  existingObjectId?: number;
  builderProps?: {
    disabledCreateTabTooltipOverride?: string;
    createEnabledOverride?: boolean;
    enableFlexibleAssociations?: boolean;
    forceSingleSelectAssociation?: boolean;
    shouldRenderCreateAndAddButton?: boolean;
    defaultProperties?: { [key: string]: string | null | undefined };
    additionalDefaultAssociations?: AdditionalDefaultAssociation[];
    experimentalUseDraggableForm?: boolean;
    extraInitialCreatorPropertyNames?: string[];
  };
}

interface OpenIframeModalData {
  width: number;
  height: number;
  url: string;
  title?: string;
  noPadding?: boolean;
  instanceId?: string;
}

interface OpenProspectingOutreachData {
  width?: number;
  height?: number;
  url: string;
  title?: string;
  crmSearchFilterGroups?: CrmSearchFilterGroup[];
  crmSearchSorts?: CrmSearchSort[];
}

// Query string Emitted from inside the prospecting outreach iframe whenever its in-modal
// navigation state changes in order to allow for deep-linking
interface ProspectingOutreachStateChangedData {
  search: string;
}

interface ShowAlertData {
  type: 'info' | 'warning' | 'success' | 'danger' | 'tip';
  message: string;
  title?: string;
}

interface OpenActivityCreatorBase {
  objectTypeId?: string;
  objectId?: number;
  windowVariant?: WindowVariant;
  taskId?: string;
  initialEmailSubject?: string;
  initialEmailBody?: string;
  mode?: 'CREATE' | 'EDIT';
  editObjectId?: number;
}

type OpenActivityCreatorGeneric = OpenActivityCreatorBase & {
  interactionType: Exclude<
    InteractionType,
    typeof EMAIL | typeof SCHEDULE | typeof TASK
  >;
};

type OpenActivityCreatorForEmail = OpenActivityCreatorBase & {
  interactionType: typeof EMAIL;
  initialContent?: EmailInitialContent;
};

type OpenActivityCreatorForSchedule = OpenActivityCreatorBase & {
  interactionType: typeof SCHEDULE;
  initialContent?: ScheduleInitialContent;
};

type OpenActivityCreatorForTask = OpenActivityCreatorBase & {
  interactionType: typeof TASK;
  initialContent?: TaskInitialContent;
};

export type OpenActivityCreator =
  | OpenActivityCreatorGeneric
  | OpenActivityCreatorForEmail
  | OpenActivityCreatorForSchedule
  | OpenActivityCreatorForTask;

export interface CloseActivityCreator {
  discardDraft?: boolean;
}

export interface OpenPlaybook {
  playbookId: string;
  saveNavigationHistory?: boolean;
}

export interface OpenPlaybookWindowData {
  playbookId: string;
  objectTypeId: string;
  objectId: string;
}

export interface SequenceEnrollment {
  enrollmentId: number;
  objectId?: number;
  sequenceName: string;
}

export interface SequenceUnenroll {
  objectId: string;
}

interface CommentsUpdated {
  objectId: string;
  objectTypeId: string;
}

interface PinnedEngagementUpdatedData {
  objectTypeId: string;
  objectId: string;
  engagementId?: string;
}

interface OpenPropertyHistoryData {
  objectTypeId: string;
  objectId: string;
  propertyNames?: string[];
  initialSearchTerm?: string;
}

interface OpenCreatePropertyPanelData {
  objectTypeId: string;
  targetCardId?: string;
}

interface OpenPropertyFileDetailsPanelData {
  objectTypeId?: string;
  objectId?: string;
  property: {
    name: string;
    externalOptionsReferenceType: string | null | undefined;
  };
  initialFileId?: string;
}

interface OpenDataAgentPanelData {
  objectTypeId: string;
}

interface OpenEnrichmentPropertiesPanelData {
  objectTypeId: string;
  objectId: string;
  propertyNames?: string[];
}

interface CloseEnrichmentPropertiesPanelData {
  objectTypeId: string;
  objectId: string;
  enriched?: boolean;
}

interface OpenContactSourcingPanelData {
  objectTypeId: string;
  objectId: string;
}

interface OpenIntentSignalsTrackingData {
  objectTypeId: '0-2';
  objectId: string;
  source?: string;
}

interface OpenIntentSignalsSidebarData {
  objectTypeId: string;
  objectId: string;
}

interface CloseContactSourcingPanelData {
  objectTypeId: string;
  objectId: string;
}

interface CloseSmartPropertiesPanelData {
  objectTypeId: string;
  objectId: string;
  enriched?: boolean;
  selectedProperties?: string[];
}

interface PollSmartPropertiesData {
  objectIds: string[];
  objectTypeId: string;
  propertyNames: string[];
}

interface OpenCardUserCustomizationDataBase {
  cardId: string;
  cardType: string;
}

export interface OpenPropertiesV3CardUserCustomizationData
  extends OpenCardUserCustomizationDataBase {
  cardType: 'PROPERTIES_V3';
  objectTypeId: string;
}

export interface OpenPropertiesCardUserCustomizationData
  extends OpenCardUserCustomizationDataBase {
  cardType: 'PROPERTIES';
  objectTypeId: string;
}

export type OpenCardUserCustomizationData =
  | OpenPropertiesV3CardUserCustomizationData
  | OpenPropertiesCardUserCustomizationData;

export interface OpenDealScoreHistoryData {
  dealId: string;
}

interface UpdateCardConfigurationData {
  cardId: string;
  cardType: string;
}

interface CollabSidebarData {
  objectId: string;
  objectTypeId: string;
  tab: 'approval' | 'comments';
}

type NO_DATA = void;

export type UpdateEmailComposerContentData = {
  subject?: string;
  body: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  isReply: boolean;
  engagementId?: string;
  replyType?: 'reply' | 'replyAll' | 'forward';
};

interface OpenRecentConversionHistoryPanelData {
  objectId: number;
  email?: string;
  firstName?: string;
  jobRole?: string;
  jobTitle?: string;
  lastName?: string;
  role?: string;
}

interface OpenDealSupportRequestPanelData {
  objectId: number;
  dealAmount: number | null;
}

interface OpenSalesManagerEscalationPanelData {
  objectId: number;
  dealAmount: number | null;
  hasSubmittedRequest: boolean;
}

interface OpenAttachmentsCardFileManagerData {
  objectTypeId: string;
  objectId: string;
}

type AttachmentToRemove = {
  hidden: boolean;
  id: number;
  // Empty will remove the attachment and detach it from all engagements. Specify engagementIds to detach from specific engagements.
  engagementIdsToDetachIfRemoved?: number[];
};
interface OpenAttachmentsRemoveModalData {
  objectTypeId: string;
  objectId: string;
  attachmentsToRemove: AttachmentToRemove[];
}

interface RecordAttachmentsRemovedData {
  attachmentsRemoved: AttachmentToRemove[];
}

interface UpdateRecordAttachmentsData {
  objectTypeId: string;
  objectId: string;
  engagementId: number;
}

type TimelineStructure = 'SIMPLE' | 'CONDENSED' | 'PREVIEW';

interface OpenActivityTimelineSidebarData {
  objectTypeId?: string;
  objectId?: number;
  panelName?: string;
  prefilters?: CrmSearchFilter[];
  prefilterGroups?: CrmSearchFilterGroup[];
  prefilterTagsOverrides?: {
    [property: string]: { label?: string; hidden?: boolean };
  };
  timelineId?: string;
  timelineStructure?: TimelineStructure;
  sorts?: CrmSearchSort[];
  width?: number;
}

export interface ActivityCreatorOpened {
  interactionType: InteractionType;
  engagementId?: string;
}

interface OpenDealSplitsSidebarData {
  dealId: string;
  isDealCollaborators?: boolean;
}

interface OpenNbaRecommendationsSidebarData {
  objectId: number;
  objectTypeId: string;
  objectName?: string;
}

type OpenDealSplitsDeleteModalData = {
  objectId: number;
};

type OpenManageOwnershipModalData = {
  objectType: string;
  objectId: number;
  canEdit?: boolean;
  hasChangeDomainUrlAccessScope?: boolean;
  currentOwnerEmail?: string;
  currentUserEmail: string;
  domain?: string;
  subjectName?: string;
};

type OpenRemoveAndRecyclePanelData = {
  objectType: string;
  objectId: number;
  ownerType: string;
};

type OpenRejectLeadModalData = {
  objectType: string;
  objectId: number;
};

type OpenDisputeDomainModalData = {
  domain: string;
};

type OpenEnrichDomainModalData = {
  domain: string;
};

type OpenChangeDomainUrlModalData = {
  objectType: string;
  objectId: number;
  domain?: string;
};

type OpenMoveContactModalData = {
  objectId: number;
};

type OpenRemoveBadFitModalData = {
  objectType: string;
  objectId: number;
};

type OpenDomainHistoryPanelData = {
  objectType: string;
  objectId: number;
  domain?: string;
  currentOwnerEmail?: string;
  selectedTab?: 'ASSIGNMENT_HISTORY' | 'REVIEW_HISTORY';
};

type DomainControlsActionCompletedData = {
  action: string;
};

type DomainControlsModalClosedData = Record<string, never>;

type OpenDealCollaboratorsDeleteModalData = {
  objectId: number;
};

type ContainerTabOpenedData = {
  tabLabel: string;
  isActivitiesTab: boolean;
  isOpenedAsDefault: boolean;
};

type OpenScoreHistoryData = {
  objectId: number;
  objectTypeId: string;
  outputProperty: string;
  appType: 'MH' | 'SvH';
  scoreId?: number;
};

interface OpenRecentProductsData {
  objectId: string;
  objectTypeId: '0-2';
  type: 'PURCHASED' | 'EXPRESSED_INTEREST';
}

type OpenReplacePinnedEngagementModal = {
  objectId: number;
  objectTypeId: string;
  engagementId: number;
  currentPinSingularForm: { lowerCase: string; upperCase: string };
  newPinSingularForm: { lowerCase: string; upperCase: string };
  canViewPinnedEngagement?: boolean;
};

type OpenDeleteEngagementModal = {
  objectId: number;
  objectTypeId: string;
  modalTextOverride?: {
    message?: string;
    description?: string;
  };
};

type OpenDeleteScheduledEmailModal = {
  objectId: number;
  objectTypeId: string;
  facsimileSendId: string;
  senderEmail: string;
};

type OpenDeleteDraftEmailModal = {
  objectId: number;
  objectTypeId: string;
};

type OpenDeleteTaskModal = {
  taskId: number;
  objectId: number;
  objectTypeId: string;
  isRepeating: boolean;
};

type FocusTaskInActiveTaskQueueData = {
  taskId: number;
};

type OpenTaskQueueData = {
  objectId: string;
  source?: string;
  shouldOpenInNewTab?: boolean;
  returnUrl?: string;
  returnToCurrentPage?: boolean;
  returnButtonLabel?: string;
  crmSearchQuery?: {
    filterGroups: CrmSearchFilterGroup[];
    sorts?: CrmSearchSort[] | null;
    offset?: number | null;
    query?: string | null;
    objectTypeId?: string;
    count?: number | null;
  };
};

type OpenWorkflowEnrollmentModalData = {
  objectId: number;
  objectTypeId: string;
};

type UpdateWorkflowEnrollmentsData = {
  objectId: number;
  objectTypeId: string;
};

type OpenSequenceEnrollmentModalData = {
  objectId: number;
  objectTypeId: string;
};

type OpenSequenceUnenrollModalData = {
  enrollmentId: number;
  contactId: number;
};

type OpenMultiAccountPanelData = {
  portalId: number;
  objectId: number;
  objectTypeId: string;
};

type OpenPortalUsersPanelData = {
  hubId: number;
};

type UpdateObjectData = {
  objectTypeId: string;
  objectId: string;
  propertyNames?: string[];
};

type UpdateLayoutData = {
  objectTypeId: string;
  objectId: string;
};

type ObjectCreated = {
  objectTypeId: string;
  objectId: string;
  preventRedirect?: boolean;
  associatedParent?: {
    objectId: string;
    objectTypeId: string;
    associationTypeId: number;
    associationCategory: string;
  };
  contextualNudge?: string;
};

type ObjectDeleted = {
  objectTypeId: string;
  objectId: string;
  isPermanentDelete?: boolean;
};

type OpenMergeObjectsModalData = {
  objectTypeId: string;
  objectId1: number;
  objectId2?: number;
  skipRedirectAfterMerge?: boolean;
};

type ObjectsMergedData = {
  objectTypeId: string;
  primaryObjectId: number;
  mergedObjectId: number;
  newObjectId: number;
};

type OpenUnmergeObjectsModalData = {
  objectTypeId: string;
  objectId: number;
  mergedObjectIds?: string;
  mergeToRevertTimestamp?: number;
};

type OpenCloneObjectsModalData = {
  objectId: number;
  objectTypeId: string;
};

type OpenReviewAssociationsModalData = {
  objectId: number;
  objectTypeId: string;
};

type OpenHideEmailTrackingModalData = {
  eventGuid: string;
  objectId: number;
  ownerId: number;
  trackerKey: string;
};

type PropertyDefinitionsUpdatedData = {
  objectTypeId: string;
  propertyNames?: string[];
};

type PropertyCreatedForCardData = {
  objectTypeId: string;
  propertyName: string;
};

type OpenAllPropertiesPanelData = {
  objectTypeId: string;
  objectId: string;
  initialSearchTerm?: string;
};

type ConsentCardEmail = {
  type: 'PRIMARY' | 'ADDITIONAL';
  emailAddress: string;
  status: 'UNKNOWN' | 'CONSENTED' | 'DENIED' | 'NOT_APPLICABLE';
};

type OpenEmailTrackingConsentPanelData = {
  contactId: number;
  brandId?: number;
  emails: ConsentCardEmail[];
};

type OpenNextStepsPanelData = {
  emailThreadId: string;
  dealId?: number;
};

type OpenUnfollowModalData = {
  objectId: string;
  objectTypeId: string;
};

type OpenAssociationHistoryPanelData = {
  fromObjectId: string;
  fromObjectTypeId: string;
  toObjectTypeId?: string;
  toObjectId?: string;
};

type OpenInfluencedCampaignsPanelData = {
  contactId: number;
};

type OpenEditCampaignEngagementModalData = {
  contactId: number;
  campaignObjectId?: string;
};

type OpenEditCampaignMarketingEventModalData = {
  marketingEventId: number;
};

type OpenEditCampaignAssetModalData = {
  objectTypeId: string;
  assetId: number | string;
};

type OpenImportContactsModalData = {
  objectId: number;
};

type OpenMarketingEventContactsModalData = {
  objectId: number;
  contactType:
    | 'TOTAL_REGISTRATIONS'
    | 'CURRENT_REGISTRATIONS'
    | 'CANCELLATIONS'
    | 'ATTENDEES'
    | 'NO_SHOWS';
};

type OpenCustomEngagementPropertySettingsData = {
  objectTypeId: string;
  source?: 'timeline' | 'activityCreator';
};

type OpenAddPoiModalData = {
  dealId: number;
};

type OpenSalesOrderWizardData = {
  cardId: string;
  dealId: string;
};

type HandoffType =
  | 'ASSISTED_SALE'
  | 'BOOK_A_MEETING'
  | 'BOOKED_MEETING'
  | 'FYI_EMAIL'
  | 'IMMEDIATE_SALE_COMPLETE'
  | 'LIVE_TRANSFER'
  | 'QUALIFIED_LEAD'
  | 'SEND_MEETING_LINK'
  | 'SEND_PAYMENT_LINK'
  | 'TOUCHLESS_PUSH';

type Contact = {
  vid: number | string;
  email?: string;
  familyName?: string;
  givenName?: string;
};

type Submitter = {
  email: string;
  familyName: string;
  givenName: string;
};

type Submission = {
  id: string;
  submittedAt: number;
  handoffType: HandoffType;
  contact: Contact;
  submitter: Submitter;
};

type OpenQualifiedLeadsHistoryPanelData = {
  submissions: Submission[];
};

type OpenRaiseALeadPanelData = {
  appName: string;
  contactVid: number;
  chatThreadId?: number;
};

// Mirrors restore-request-side-panel-lib's CrmChangesInitialValues, so the bus
// doesn't depend on that package for its types.
type CrmChangesInitialValues = {
  channelId?: number;
  sourceType?:
    | 'CRM_CHANGES'
    | 'IMPORT'
    | 'WORKFLOW'
    | 'INTEGRATION'
    | 'CHATSPOT'
    | 'CUSTOMER_AGENT'
    | 'BREEZE_AGENT';
  objectTypeIds?: string[];
  sourceFieldId?: number;
  sourceFilterId?: number;
  userId?: number;
};

type OpenRestoreRequestSidePanelData = {
  restoreType: 'crmChanges';
  source?: string;
  crmChangesOptions?: {
    initialValues?: CrmChangesInitialValues;
  };
};

type OpenExistingSequenceEnrollmentModalData = {
  enrollmentId: number;
  modalType: 'EDIT' | 'VIEW';
};

type OpenInvoiceModalData = {
  objectId?: string;
  objectTypeId?: string;
  operation: 'EDIT' | 'CLONE' | 'CREATE_BLANK_INVOICE' | 'CONVERT_TO_INVOICE';
};

type AssociationTuple = [number] | [];
type SubscriptionAssociations = {
  SUBSCRIPTION_TO_TICKET: number[];
  SUBSCRIPTION_TO_CONTRACT: AssociationTuple;
  SUBSCRIPTION_TO_PAYMENT_LINK: AssociationTuple;
  SUBSCRIPTION_TO_COMMERCE_PAYMENT: number[];
  SUBSCRIPTION_TO_INVOICE: number[];
  SUBSCRIPTION_TO_QUOTE: AssociationTuple;
  SUBSCRIPTION_TO_CONTACT: AssociationTuple;
  SUBSCRIPTION_TO_COMPANY: AssociationTuple;
  SUBSCRIPTION_TO_DEAL: number[];
};
type PartialSubscriptionAssociations = NullableOfType<
  Partial<SubscriptionAssociations>
>;

type OpenBillingWizardModalData = {
  fromObjectId: Nullable<number>;
  fromObjectTypeId: Nullable<string>;
  origin: Nullable<string>;
  contactId: Nullable<number>;
  companyId: Nullable<number>;
  associations: Nullable<PartialSubscriptionAssociations>;
};

type OpenSubscriptionsAdminModalData = {
  subscriptionId: number;
};

type OpenInvoiceActionData = {
  objectId: number;
  action:
    | 'VOID'
    | 'DELETE'
    | 'EDIT_EMAIL_REMINDERS'
    | 'RECORD_PAYMENT'
    | 'CHARGE_PAYMENT_METHOD'
    | 'APPLY_MANAGE_CREDIT';
  source: 'sidebar-card' | 'highlight-card';
};

type OpenInvoiceSendEmailModalData = {
  invoiceId: number;
  trackingLocation:
    | 'invoicesEditor'
    | 'invoicesHighlightCard'
    | 'invoicesSidebarCard'
    | 'aiSummaryObjectCard'
    | 'contractPerformanceCard';
};

type OpenCreditMemoModalData = {
  objectId?: string;
  objectTypeId?: string;
  operation: 'CREATE' | 'EDIT';
};

type OpenProspectingAgentModalData = {
  objectIds: number[];
  objectTypeId: '0-1' | '0-2';
};

type OpenRoutingAssignmentDetailsPanelData = {
  targetObjectId: number;
  targetObjectType: string;
  timestamp: number;
  userId?: string;
  source?: string;
};

interface ProspectingAgentEnrollment {
  contactIds: number[];
  contactAssignmentIds: number[];
}

interface ProspectingAgentUnenrollment {
  contactIds: number[];
  contactAssignmentIds: number[];
}

type OpenCallTranscriptSidebarData = {
  objectId: string;
};

interface OpenActivityQuickViewModalData {
  id: string;
  ownerId: string;
  userLastModified: string;
  userLastModifiedEmail: string;
}

type SmartDataCaptureQueryObject = {
  sources?: {
    sourceObjectId: number;
    sourceObjectTypeId: string;
  }[];
  targets?: {
    targetObjectId?: number;
    targetObjectTypeId: string;
  }[];
  count?: number;
};

type OpenSmartDataCaptureModalData = {
  config?: { fetchMode: 'bulk_record'; objectTypeId: string };
  queryObject?: SmartDataCaptureQueryObject;
};

type QuoteEditorFlowType = 'create' | 'edit' | 'approve';

type QuoteEditorEmbedSource =
  | 'dealRecord'
  | 'dealLineItemEditor'
  | 'quoteTemplateSettings'
  | 'quotePreviewPanel'
  | 'sharePaymentsApp'
  | 'kitchenSink'
  | 'contactRecord'
  | 'companyRecord'
  | 'contractRecord';

type OpenQuoteEditorData = {
  flowType: QuoteEditorFlowType;
  quoteId?: string | null;
  dealId?: string | null;
  embedSource: QuoteEditorEmbedSource;
  forceCreateLegacyQuote?: boolean;
};

type QuoteEditorClosedData = {
  quoteId?: string | null;
  dealId?: string | null;
  embedSource: QuoteEditorEmbedSource;
};

// A narrower set than QuoteEditorEmbedSource: only the sources CreateQuoteSidebar's
// LOCATION enum can represent. Keeping this distinct (rather than reusing
// QuoteEditorEmbedSource) makes an unsupported sender a compile-time error instead of
// a silently un-attributed tracking location.
type CpqQuoteSidebarEmbedSource =
  | 'dealRecord'
  | 'dealLineItemEditor'
  | 'quotePreviewPanel'
  | 'sharePaymentsApp'
  | 'contactRecord'
  | 'companyRecord'
  | 'contractRecord';

type OpenCpqQuoteSidebarData = {
  embedSource: CpqQuoteSidebarEmbedSource;
};

type OpenProductEditorData = {
  objectId?: string | null;
  isProductBundle: boolean;
};

type OpenPriceBookAutoAssignmentModalData = {
  objectId?: string | null;
};

interface OpenPaymentLinkIframeData {
  objectId: number;
  objectTypeId: string;
}

interface OpenFlywheelColdSourceModalData {
  companyId: number;
}

interface OpenPaymentLinkAssociationPanelData {
  fromObjectId: number;
  fromObjectTypeId: string;
  toObjectTypeId: string;
  associationTypeId: number;
  associationCategory: string;
}

interface OpenPaymentLinkCreateModalData {
  fromObjectId: number;
  fromObjectTypeId: string;
  toObjectTypeId: string;
  associationTypeId: number;
  associationCategory: string;
  operation: 'CREATE' | 'CONVERT_DEAL';
}

type OpenCreatePaymentPlanModalData = {
  objectId: string;
  balanceDue: number;
  currencyCode: string;
  /** Unix timestamp in milliseconds */
  initialDueDate: number;
  trackingLocation: string;
};

type OpenEditPaymentPlanModalData = {
  paymentPlanId: string;
  trackingLocation: string;
};

type OpenCancelPaymentPlanModalData = {
  paymentPlanId: string;
};

type PaymentPlanUpdatedData = {
  paymentPlanId?: string;
};

type OpenBuyingGroupCanvasModalData = {
  objectId: number;
  objectTypeId: string;
  sourceObjectId?: number;
  sourceObjectTypeId?: string;
  title?: string;
  cardType?: string;
  // Surface identifier forwarded to the modal's `surface` prop, which the
  // embedded Breeze chat surfaces to the model (under the `currentApp` key).
  // This topic always opens the interactive fullscreen modal, so the only
  // meaningful value is 'buying-groups-modal'; kept optional so previews/older
  // senders omit it. The literal stays assignable to buying-groups-lib's
  // BuyingGroupsSurface type.
  surface?: 'buying-groups-modal';
};
type OpenAccountMapCanvasModalData = {
  objectId: number;
  objectTypeId: '0-2';
  title?: string;
  cardType?: string;
};
type ViewUpdateCompletedData = {
  objectTypeId: string;
  success: boolean;
  error?: Error;
};

// Copied from crm-cards-sdk/static/js/types.ts to avoid circular dependency
type Primatives =
  | string
  | number
  | boolean
  | null
  | undefined
  | SerializableConfigObject;
type PrimativeOrArray = Primatives | Primatives[];
type RecordOf<T> = Record<string, T>;
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SerializableConfigObject extends RecordOf<PrimativeOrArray> {}

type CardConfigChangedData = {
  cardId: string;
  cardType: string;
  configuration: SerializableConfigObject;
};

type CardOrderChangedData = {
  location: string;
  newCardOrder: string[];
  objectTypeId?: string;
  previousCardOrder: string[];
  viewId?: string;
};

type SelectContainerTabData = {
  tabId: string;
  containerId: string;
};

type ExpandCollapsibleContainerData = {
  containerId: string;
};

type ExpandCollapsibleCardData = {
  cardNodeId: string;
};

export type TopicsMap = {
  ADD_ENGAGEMENT: (data: {
    id: string;
    type: string;
    isFollowUpTask?: boolean;
    eventData?: TimelineEventData;
  }) => void;
  CONTAINER_TAB_OPENED: (data: ContainerTabOpenedData) => void;
  DISPATCHED_EVENT: (data: DispatchData) => void;
  UPDATE_ASSOCIATIONS: (data: {
    objectTypeId: string;
    objectId: string;
    toObjectTypeId?: string;
    actionType?: string; //Action that originated the message, i.e. ADD_ASSOCIATION, REMOVE_ASSOCIATION...
  }) => void;
  /**
   * @deprecated OBJECTS_CREATED should be used instead.
   */
  OBJECT_CREATED: (data: ObjectCreated) => void;
  OBJECTS_CREATED: (data: ObjectCreated[]) => void;
  /**
   * @deprecated OBJECTS_DELETED should be used instead.
   */
  OBJECT_DELETED: (data: ObjectDeleted) => void;
  OBJECTS_DELETED: (data: ObjectDeleted[]) => void;
  /**
   * @deprecated UPDATE_OBJECTS should be used instead.
   */
  UPDATE_OBJECT: (data: UpdateObjectData) => void;
  UPDATE_OBJECTS: (data: UpdateObjectData[]) => void;
  LAYOUT_UPDATED: (data: UpdateLayoutData) => void;
  PREVIEW_OBJECT: (data: { objectTypeId: string; objectId: string }) => void;
  OPEN_UNIVERSAL_RECORD: (data: {
    objectTypeId: string;
    objectId: string;
    pagingInit?: {
      objectIds: string[];
      index: number;
      viewName?: string;
    };
  }) => void;
  UNIVERSAL_RECORD_OPENED: (data: {
    objectTypeId: string;
    objectId: string;
  }) => void;
  UNIVERSAL_RECORD_CLOSED: (data: NO_DATA) => void;
  CLOSE_UNIVERSAL_RECORD: (data: NO_DATA) => void;
  CLOSE_PREVIEW_OBJECT: (data: NO_DATA) => void;
  OPEN_WORK_OBJECT_PREVIEW: (data: {
    objectTypeId: string;
    objectId: string;
  }) => void;
  CLOSE_WORK_OBJECT_PREVIEW: (data: NO_DATA) => void;
  PREVIEW_ENGAGEMENT: (data: { engagementId: string }) => void;
  OPEN_ACTIVITY_CREATOR: (data: OpenActivityCreator) => void;
  CLOSE_ACTIVITY_CREATOR: (data: CloseActivityCreator) => void;
  ACTIVITY_CREATOR_OPENED: (data: ActivityCreatorOpened) => void;
  ACTIVITY_CREATOR_CLOSED: (data: CloseActivityCreator) => void;
  OPEN_ACTIVITY_QUICK_VIEW_MODAL: (
    data: OpenActivityQuickViewModalData
  ) => void;
  OPEN_CALL_WIDGET: (data: OpenActivityCreator) => void;
  OPEN_OBJECT_BUILDER: (data: OpenObjectBuilderData) => void;
  CLOSE_OBJECT_BUILDER: (data: NO_DATA) => void;
  OBJECT_BUILDER_CLOSED: (data: {
    objectTypeId: string;
    createdObjectId?: string;
  }) => void;
  OPEN_UPDATE_ASSOCIATION_MODAL: (data: OpenUpdateAssociationModalData) => void;
  OPEN_REPLACE_BILLING_CONTACT_MODAL: (
    data: OpenReplaceBillingContactModalData
  ) => void;
  OPEN_PROPERTY_DOCUMENTATION: (data: OpenPropertyDocumentationData) => void;
  OPEN_IFRAME_MODAL: (data: OpenIframeModalData) => void;
  IFRAME_MODAL_CLOSED: (data: { instanceId?: string }) => void;
  SHOW_ALERT: (data: ShowAlertData) => void;
  OPEN_PLAYBOOK: (data: OpenPlaybook) => void;
  OPEN_PLAYBOOK_WINDOW: (data: OpenPlaybookWindowData) => void;
  SEQUENCE_ENROLLMENT: (data: SequenceEnrollment) => void;
  SEQUENCE_UNENROLL: (data: SequenceUnenroll) => void;
  COMMENTS_UPDATED: (data: CommentsUpdated) => void;
  PINNED_ENGAGEMENT_UPDATED: (data: PinnedEngagementUpdatedData) => void;
  INTERNAL_REGISTER_COMMAND_SUBSCRIBED: (data: { topic: TopicName }) => void;
  INTERNAL_REGISTER_COMMAND_UNSUBSCRIBED: (data: { topic: TopicName }) => void;
  OPEN_PROPERTY_HISTORY: (data: OpenPropertyHistoryData) => void;
  CLOSE_PROPERTY_HISTORY: (data: NO_DATA) => void;
  OPEN_CREATE_PROPERTY_PANEL: (data: OpenCreatePropertyPanelData) => void;
  OPEN_PROPERTY_FILE_DETAILS_PANEL: (
    data: OpenPropertyFileDetailsPanelData
  ) => void;
  OPEN_DATA_AGENT_PANEL: (data: OpenDataAgentPanelData) => void;
  OPEN_ENRICHMENT_PROPERTIES_PANEL: (
    data: OpenEnrichmentPropertiesPanelData
  ) => void;
  OPEN_SMART_PROPERTIES_PANEL: (
    data: OpenEnrichmentPropertiesPanelData
  ) => void;
  CLOSE_ENRICHMENT_PROPERTIES_PANEL: (
    data: CloseEnrichmentPropertiesPanelData
  ) => void;
  CLOSE_SMART_PROPERTIES_PANEL: (data: CloseSmartPropertiesPanelData) => void;
  POLL_SMART_PROPERTIES: (data: PollSmartPropertiesData) => void;
  OPEN_CONTACT_SOURCING_PANEL: (data: OpenContactSourcingPanelData) => void;
  CLOSE_CONTACT_SOURCING_PANEL: (data: CloseContactSourcingPanelData) => void;
  OPEN_INTENT_SIGNALS_TRACKING: (data: OpenIntentSignalsTrackingData) => void;
  OPEN_INTENT_SIGNALS_SIDEBAR: (data: OpenIntentSignalsSidebarData) => void;
  OPEN_CARD_USER_CUSTOMIZATION: (data: OpenCardUserCustomizationData) => void;
  OPEN_DEAL_SCORE_HISTORY: (data: OpenDealScoreHistoryData) => void;
  UPDATE_CARD_CONFIGURATION: (data: UpdateCardConfigurationData) => void;
  OPEN_COLLAB_SIDEBAR: (data: CollabSidebarData) => void;
  UPDATE_APPROVAL_CARD: (data: NO_DATA) => void;
  UPDATE_RECORD_ATTACHMENTS: (data: UpdateRecordAttachmentsData) => void;
  OPEN_ATTACHMENTS_MODAL: (data: {
    objectTypeId: string;
    objectId: string;
    initialSearchTerm?: string;
  }) => void;
  OPEN_ATTACHMENTS_FILE_MANAGER: (
    data: OpenAttachmentsCardFileManagerData
  ) => void;
  OPEN_ATTACHMENTS_REMOVE_MODAL: (data: OpenAttachmentsRemoveModalData) => void;
  RECORD_ATTACHMENTS_REMOVED: (data: RecordAttachmentsRemovedData) => void;
  OPEN_MANAGE_RECORD_ACCESS_PANEL: (data: {
    objectTypeId: string;
    objectIds: string[];
  }) => void;
  CLOSE_MANAGE_RECORD_ACCESS_PANEL: (data: NO_DATA) => void;
  OPEN_SHARE_ENGAGEMENT_ACCESS_MODAL: (data: {
    engagementType: string;
    engagementId: string;
  }) => void;
  OPEN_ACTIVITY_TIMELINE_SIDEBAR: (
    data: OpenActivityTimelineSidebarData
  ) => void;
  CLOSE_ACTIVITY_TIMELINE_SIDEBAR: (data: NO_DATA) => void;
  OPEN_DEAL_SPLITS_SIDEBAR: (data: OpenDealSplitsSidebarData) => void;
  OPEN_DEAL_SPLITS_DELETE_MODAL: (data: OpenDealSplitsDeleteModalData) => void;
  DEAL_SPLIT_DELETED: (data: NO_DATA) => void;
  OPEN_DEAL_COLLABORATORS_DELETE_MODAL: (
    data: OpenDealCollaboratorsDeleteModalData
  ) => void;
  DEAL_COLLABORATORS_DELETED: (data: NO_DATA) => void;
  OPEN_SCORE_HISTORY: (data: OpenScoreHistoryData) => void;
  OPEN_RECENT_PRODUCTS: (data: OpenRecentProductsData) => void;
  OPEN_DEAL_REGISTRATION_SUMMARY: (data: {
    dealRegistrationId: number;
  }) => void;
  CLOSE_DEAL_REGISTRATION_SUMMARY: (data: NO_DATA) => void;
  OPEN_WORKFLOW_ENROLLMENT_MODAL: (
    data: OpenWorkflowEnrollmentModalData
  ) => void;
  UPDATE_WORKFLOW_ENROLLMENTS: (data: UpdateWorkflowEnrollmentsData) => void;
  OPEN_MULTI_ACCOUNT_PANEL: (data: OpenMultiAccountPanelData) => void;
  OPEN_PORTAL_USERS_PANEL: (data: OpenPortalUsersPanelData) => void;
  OPEN_DELETE_ENGAGEMENT_MODAL: (data: OpenDeleteEngagementModal) => void;
  OPEN_DELETE_SCHEDULED_EMAIL_MODAL: (
    data: OpenDeleteScheduledEmailModal
  ) => void;
  OPEN_DELETE_DRAFT_EMAIL_MODAL: (data: OpenDeleteDraftEmailModal) => void;
  OPEN_DELETE_TASK_MODAL: (data: OpenDeleteTaskModal) => void;
  OPEN_TASK_QUEUE: (data: OpenTaskQueueData) => void;
  FOCUS_TASK_IN_ACTIVE_TASK_QUEUE: (
    data: FocusTaskInActiveTaskQueueData
  ) => void;
  OPEN_REPLACE_PINNED_ENGAGEMENT_MODAL: (
    data: OpenReplacePinnedEngagementModal
  ) => void;
  OPEN_MERGE_OBJECTS_MODAL: (data: OpenMergeObjectsModalData) => void;
  OBJECTS_MERGED: (data: ObjectsMergedData) => void;
  OPEN_UNMERGE_OBJECTS_MODAL: (data: OpenUnmergeObjectsModalData) => void;
  OPEN_CLONE_OBJECTS_MODAL: (data: OpenCloneObjectsModalData) => void;
  OPEN_REVIEW_ASSOCIATIONS_MODAL: (
    data: OpenReviewAssociationsModalData
  ) => void;
  COMMAND_HANDLER_PING: (data: NO_DATA) => void;
  COMMAND_HANDLER_PONG: (data: NO_DATA) => void;
  OPEN_SEQUENCE_ENROLLMENT_MODAL: (
    data: OpenSequenceEnrollmentModalData
  ) => void;
  OPEN_HIDE_EMAIL_TRACKING_MODAL: (
    data: OpenHideEmailTrackingModalData
  ) => void;
  PROPERTY_DEFINITIONS_UPDATED: (data: PropertyDefinitionsUpdatedData) => void;
  PROPERTY_CREATED_FOR_CARD: (data: PropertyCreatedForCardData) => void;
  INDEX_VIEW_CREATED: (data: { objectTypeId: string; viewId: string }) => void;
  OPEN_ALL_PROPERTIES_PANEL: (data: OpenAllPropertiesPanelData) => void;
  CLOSE_ALL_PROPERTIES_PANEL: (data: NO_DATA) => void;
  OPEN_EMAIL_TRACKING_CONSENT_PANEL: (
    data: OpenEmailTrackingConsentPanelData
  ) => void;
  CLOSE_EMAIL_TRACKING_CONSENT_PANEL: (data: NO_DATA) => void;
  OPEN_NEXT_STEPS_PANEL: (data: OpenNextStepsPanelData) => void;
  OPEN_ASSOCIATION_HISTORY_PANEL: (
    data: OpenAssociationHistoryPanelData
  ) => void;
  CLOSE_ASSOCIATION_HISTORY_PANEL: (data: NO_DATA) => void;
  OPEN_INFLUENCED_CAMPAIGNS_PANEL: (
    data: OpenInfluencedCampaignsPanelData
  ) => void;
  OPEN_EDIT_CAMPAIGN_ENGAGEMENT_MODAL: (
    data: OpenEditCampaignEngagementModalData
  ) => void;
  OPEN_EDIT_CAMPAIGN_MARKETING_EVENT_MODAL: (
    data: OpenEditCampaignMarketingEventModalData
  ) => void;
  OPEN_EDIT_CAMPAIGN_ASSET_MODAL: (
    data: OpenEditCampaignAssetModalData
  ) => void;
  CREATE_COLUMN_INSIGHTS: (data: {
    columnName: string;
    filterGroups: CrmSearchFilterGroup[];
    objectTypeId: string;
    query?: string;
    propertyType?: string;
    numberDisplayHint?: string;
  }) => void;
  CRM_OBJECT_TABLE_ADD_COLUMNS: (data: {
    columns: string[];
    objectTypeId: string;
  }) => void;
  OPEN_CONTRACT_TERMINATE_MODAL: (data: { contractId: number }) => void;
  OPEN_CONTRACT_MANAGE_TERMINATION_MODAL: (data: {
    contractId: number;
    terminationDate?: string | null;
  }) => void;
  OPEN_CONTRACT_CHANGE_QUOTE_MODAL: (data: { contractId: number }) => void;
  OPEN_CONTRACT_CANCEL_CHANGE_QUOTE_MODAL: (data: {
    contractId: number;
    changeId?: number;
  }) => void;
  OPEN_CONTRACT_UPDATE_PAYMENT_METHOD_MODAL: (data: {
    contractId: number;
  }) => void;
  OPEN_CONTRACT_RENEWAL_QUOTE_MODAL: (data: { contractId: number }) => void;
  OPEN_CONTRACT_CANCEL_RENEWAL_QUOTE_MODAL: (data: {
    contractId: number;
    changeId?: number;
  }) => void;
  OPEN_CONTRACT_CANCEL_TERMINATION_MODAL: (data: {
    contractId: number;
  }) => void;
  OPEN_CONTRACT_DELETE_MODAL: (data: { contractId: number }) => void;
  OPEN_CONTRACT_EDIT_MODAL: (data: { contractId: number }) => void;
  OPEN_CONTRACT_RENEW_MODAL: (data: { contractId: number }) => void;
  OPEN_CONTRACT_PAUSE_MODAL: (data: { contractId: number }) => void;
  OPEN_CONTRACT_RESUME_MODAL: (data: { contractId: number }) => void;
  CONTRACT_ACTION_COMPLETED: (data: {
    contractId: number;
    action:
      | 'CREATE_CHANGE_QUOTE'
      | 'CANCEL_CHANGE_QUOTE'
      | 'CANCEL_CHANGE_DIRECT'
      | 'CREATE_RENEWAL_QUOTE'
      | 'CANCEL_RENEWAL_QUOTE'
      | 'RESCHEDULE_TERMINATION'
      | 'CANCEL_TERMINATION'
      | 'TERMINATE'
      | 'UPDATE_PAYMENT_METHOD'
      | 'DELETE_CONTRACT'
      | 'MARK_AS_COMPLETE'
      | 'RENEW'
      | 'PAUSE'
      | 'RESUME'
      | 'UPDATE_RESUME_DATE';
  }) => void;
  OPEN_CREATE_DEAL_PLAN_MODAL: (data: { dealId: number }) => void;
  DEAL_PLAN_CREATED: (data: { dealId: number }) => void;
  OPEN_DELETE_DEAL_PLAN_MODAL: (data: {
    dealId: number;
    isShared: boolean;
  }) => void;
  DEAL_PLAN_DELETED: (data: { dealId: number }) => void;
  OPEN_EXISTING_SEQUENCE_ENROLLMENT_MODAL: (
    data: OpenExistingSequenceEnrollmentModalData
  ) => void;
  OPEN_INVOICE_MODAL: (data: OpenInvoiceModalData) => void;
  OPEN_INVOICE_ACTION_MODAL: (data: OpenInvoiceActionData) => void;
  OPEN_INVOICE_SEND_EMAIL_MODAL: (data: OpenInvoiceSendEmailModalData) => void;
  OPEN_CREDIT_MEMO_MODAL: (data: OpenCreditMemoModalData) => void;
  OPEN_BILLING_WIZARD_MODAL: (data: OpenBillingWizardModalData) => void;
  OPEN_SUBSCRIPTIONS_ADMIN_MODAL: (
    data: OpenSubscriptionsAdminModalData
  ) => void;
  OPEN_SEQUENCE_UNENROLL_MODAL: (data: OpenSequenceUnenrollModalData) => void;
  OPEN_PROSPECTING_AGENT_MODAL: (data: OpenProspectingAgentModalData) => void;
  OPEN_ROUTING_ASSIGNMENT_DETAILS_PANEL: (
    data: OpenRoutingAssignmentDetailsPanelData
  ) => void;
  PROSPECTING_AGENT_ENROLLMENT: (data: ProspectingAgentEnrollment) => void;
  PROSPECTING_AGENT_UNENROLLMENT: (data: ProspectingAgentUnenrollment) => void;
  OPEN_CALL_TRANSCRIPT_SIDEBAR: (data: OpenCallTranscriptSidebarData) => void;
  CLOSE_CALL_TRANSCRIPT_SIDEBAR: (data: NO_DATA) => void;
  GOOGLE_DRIVE_INTEGRATION: (data: GoogleDriveIntegrationEvent) => void;
  OPEN_SMART_DATA_CAPTURE_MODAL: (data: OpenSmartDataCaptureModalData) => void;
  UPDATE_SMART_DATA_PROPERTY_SUGGESTIONS: (data: NO_DATA) => void;
  CARD_CONFIG_CHANGED: (data: CardConfigChangedData) => void;
  CARD_ORDER_CHANGED: (data: CardOrderChangedData) => void;
  CLOSE_PREVIEW_ENGAGEMENT: (data: { engagementId: string }) => void;
  OPEN_BUYING_GROUP_CANVAS_MODAL: (
    data: OpenBuyingGroupCanvasModalData
  ) => void;
  BUYING_GROUP_CANVAS_MODAL_CLOSED: (data: { buyingGroupId: number }) => void;
  OPEN_ACCOUNT_MAP_CANVAS_MODAL: (data: OpenAccountMapCanvasModalData) => void;
  ACCOUNT_MAP_CANVAS_UPDATED: (data: { objectId: number }) => void;
  OPEN_IMPORT_CONTACTS_MODAL: (data: OpenImportContactsModalData) => void;
  OPEN_MARKETING_EVENT_CONTACTS_MODAL: (
    data: OpenMarketingEventContactsModalData
  ) => void;
  OPEN_ADD_POI_MODAL: (data: OpenAddPoiModalData) => void;
  OPEN_SALES_ORDER_WIZARD: (data: OpenSalesOrderWizardData) => void;
  OPEN_QUALIFIED_LEADS_HISTORY_PANEL: (
    data: OpenQualifiedLeadsHistoryPanelData
  ) => void;
  OPEN_RAISE_A_LEAD_PANEL: (data: OpenRaiseALeadPanelData) => void;
  VIEW_UPDATE_REQUESTED: (data: NO_DATA) => void;
  VIEW_UPDATE_COMPLETED: (data: ViewUpdateCompletedData) => void;
  VIEW_WORKING_LAYER_UPDATED: (data: {
    viewId: string;
    objectTypeId: string;
  }) => void;
  VIEW_WORKING_LAYER_RESET: (data: {
    viewId: string;
    objectTypeId: string;
    status: 'started' | 'succeeded';
  }) => void;
  OPEN_CUSTOM_ENGAGEMENT_PROPERTY_SETTINGS: (
    data: OpenCustomEngagementPropertySettingsData
  ) => void;
  OPEN_RESTORE_REQUEST_SIDE_PANEL: (
    data: OpenRestoreRequestSidePanelData
  ) => void;
  OPEN_RECENT_CONVERSION_HISTORY_PANEL: (
    data: OpenRecentConversionHistoryPanelData
  ) => void;
  OPEN_DEAL_SUPPORT_REQUEST_PANEL: (
    data: OpenDealSupportRequestPanelData
  ) => void;
  OPEN_SALES_MANAGER_ESCALATION_PANEL: (
    data: OpenSalesManagerEscalationPanelData
  ) => void;
  DEAL_SUPPORT_REQUEST_SUBMITTED: (data: NO_DATA) => void;
  SALES_MANAGER_ESCALATION_SUBMITTED: (data: NO_DATA) => void;
  UPDATE_EMAIL_COMPOSER_CONTENT: (data: UpdateEmailComposerContentData) => void;
  OPEN_UNFOLLOW_MODAL: (data: OpenUnfollowModalData) => void;
  OPEN_MANAGE_OWNERSHIP_MODAL: (data: OpenManageOwnershipModalData) => void;
  OPEN_REMOVE_AND_RECYCLE_PANEL: (data: OpenRemoveAndRecyclePanelData) => void;
  OPEN_REJECT_LEAD_MODAL: (data: OpenRejectLeadModalData) => void;
  OPEN_DISPUTE_DOMAIN_MODAL: (data: OpenDisputeDomainModalData) => void;
  OPEN_ENRICH_DOMAIN_MODAL: (data: OpenEnrichDomainModalData) => void;
  OPEN_CHANGE_DOMAIN_URL_MODAL: (data: OpenChangeDomainUrlModalData) => void;
  OPEN_MOVE_CONTACT_MODAL: (data: OpenMoveContactModalData) => void;
  OPEN_REMOVE_BAD_FIT_MODAL: (data: OpenRemoveBadFitModalData) => void;
  OPEN_DOMAIN_HISTORY_PANEL: (data: OpenDomainHistoryPanelData) => void;
  DOMAIN_CONTROLS_ACTION_COMPLETED: (
    data: DomainControlsActionCompletedData
  ) => void;
  DOMAIN_CONTROLS_MODAL_CLOSED: (data: DomainControlsModalClosedData) => void;
  OPEN_QUOTE_EDITOR: (data: OpenQuoteEditorData) => void;
  OPEN_CPQ_QUOTE_SIDEBAR: (data: OpenCpqQuoteSidebarData) => void;
  OPEN_PROSPECTING_OUTREACH: (data: OpenProspectingOutreachData) => void;
  CLOSE_PROSPECTING_OUTREACH: (data: NO_DATA) => void;
  PROSPECTING_OUTREACH_STATE_CHANGED: (
    data: ProspectingOutreachStateChangedData
  ) => void;
  QUOTE_EDITOR_CLOSED: (data: QuoteEditorClosedData) => void;
  OPEN_PRODUCT_EDITOR: (data: OpenProductEditorData) => void;
  OPEN_PRICE_BOOK_AUTO_ASSIGNMENT_MODAL: (
    data: OpenPriceBookAutoAssignmentModalData
  ) => void;
  OPEN_CREATE_PAYMENT_PLAN: (data: OpenCreatePaymentPlanModalData) => void;
  OPEN_EDIT_PAYMENT_PLAN: (data: OpenEditPaymentPlanModalData) => void;
  OPEN_CANCEL_PAYMENT_PLAN: (data: OpenCancelPaymentPlanModalData) => void;
  OPEN_PAYMENT_LINK_IFRAME: (data: OpenPaymentLinkIframeData) => void;
  OPEN_FLYWHEEL_COLD_SOURCE_MODAL: (
    data: OpenFlywheelColdSourceModalData
  ) => void;
  OPEN_PAYMENT_LINK_ASSOCIATION_PANEL: (
    data: OpenPaymentLinkAssociationPanelData
  ) => void;
  OPEN_PAYMENT_LINK_CREATE_MODAL: (
    data: OpenPaymentLinkCreateModalData
  ) => void;
  PAYMENT_PLAN_UPDATED: (data: PaymentPlanUpdatedData) => void;
  OPEN_NBA_RECOMMENDATIONS_SIDEBAR: (
    data: OpenNbaRecommendationsSidebarData
  ) => void;
  SELECT_CONTAINER_TAB: (data: SelectContainerTabData) => void;
  EXPAND_RIGHT_SIDEBAR: (data: NO_DATA) => void;
  RIGHT_SIDEBAR_EXPANDED: (data: NO_DATA) => void;
  EXPAND_COLLAPSIBLE_CONTAINER: (data: ExpandCollapsibleContainerData) => void;
  EXPAND_COLLAPSIBLE_CARD: (data: ExpandCollapsibleCardData) => void;
};

export const TOPIC_NAMES: { [topic in keyof TopicsMap]: topic } = {
  ADD_ENGAGEMENT: 'ADD_ENGAGEMENT',
  CONTAINER_TAB_OPENED: 'CONTAINER_TAB_OPENED',
  DISPATCHED_EVENT: 'DISPATCHED_EVENT',
  UPDATE_ASSOCIATIONS: 'UPDATE_ASSOCIATIONS',
  OBJECT_CREATED: 'OBJECT_CREATED',
  OBJECTS_CREATED: 'OBJECTS_CREATED',
  OBJECT_DELETED: 'OBJECT_DELETED',
  OBJECTS_DELETED: 'OBJECTS_DELETED',
  UPDATE_OBJECT: 'UPDATE_OBJECT',
  UPDATE_OBJECTS: 'UPDATE_OBJECTS',
  LAYOUT_UPDATED: 'LAYOUT_UPDATED',
  PREVIEW_OBJECT: 'PREVIEW_OBJECT',
  OPEN_UNIVERSAL_RECORD: 'OPEN_UNIVERSAL_RECORD',
  UNIVERSAL_RECORD_OPENED: 'UNIVERSAL_RECORD_OPENED',
  UNIVERSAL_RECORD_CLOSED: 'UNIVERSAL_RECORD_CLOSED',
  CLOSE_UNIVERSAL_RECORD: 'CLOSE_UNIVERSAL_RECORD',
  PREVIEW_ENGAGEMENT: 'PREVIEW_ENGAGEMENT',
  CLOSE_PREVIEW_OBJECT: 'CLOSE_PREVIEW_OBJECT',
  OPEN_WORK_OBJECT_PREVIEW: 'OPEN_WORK_OBJECT_PREVIEW',
  CLOSE_WORK_OBJECT_PREVIEW: 'CLOSE_WORK_OBJECT_PREVIEW',
  OPEN_ACTIVITY_CREATOR: 'OPEN_ACTIVITY_CREATOR',
  CLOSE_ACTIVITY_CREATOR: 'CLOSE_ACTIVITY_CREATOR',
  ACTIVITY_CREATOR_OPENED: 'ACTIVITY_CREATOR_OPENED',
  ACTIVITY_CREATOR_CLOSED: 'ACTIVITY_CREATOR_CLOSED',
  OPEN_ACTIVITY_QUICK_VIEW_MODAL: 'OPEN_ACTIVITY_QUICK_VIEW_MODAL',
  OPEN_CALL_WIDGET: 'OPEN_CALL_WIDGET',
  OPEN_OBJECT_BUILDER: 'OPEN_OBJECT_BUILDER',
  CLOSE_OBJECT_BUILDER: 'CLOSE_OBJECT_BUILDER',
  OBJECT_BUILDER_CLOSED: 'OBJECT_BUILDER_CLOSED',
  OPEN_UPDATE_ASSOCIATION_MODAL: 'OPEN_UPDATE_ASSOCIATION_MODAL',
  OPEN_REPLACE_BILLING_CONTACT_MODAL: 'OPEN_REPLACE_BILLING_CONTACT_MODAL',
  OPEN_PROPERTY_DOCUMENTATION: 'OPEN_PROPERTY_DOCUMENTATION',
  OPEN_IFRAME_MODAL: 'OPEN_IFRAME_MODAL',
  IFRAME_MODAL_CLOSED: 'IFRAME_MODAL_CLOSED',
  SHOW_ALERT: 'SHOW_ALERT',
  OPEN_PLAYBOOK: 'OPEN_PLAYBOOK',
  OPEN_PLAYBOOK_WINDOW: 'OPEN_PLAYBOOK_WINDOW',
  SEQUENCE_ENROLLMENT: 'SEQUENCE_ENROLLMENT',
  SEQUENCE_UNENROLL: 'SEQUENCE_UNENROLL',
  COMMENTS_UPDATED: 'COMMENTS_UPDATED',
  PINNED_ENGAGEMENT_UPDATED: 'PINNED_ENGAGEMENT_UPDATED',
  INTERNAL_REGISTER_COMMAND_SUBSCRIBED: 'INTERNAL_REGISTER_COMMAND_SUBSCRIBED',
  INTERNAL_REGISTER_COMMAND_UNSUBSCRIBED:
    'INTERNAL_REGISTER_COMMAND_UNSUBSCRIBED',
  OPEN_PROPERTY_HISTORY: 'OPEN_PROPERTY_HISTORY',
  CLOSE_PROPERTY_HISTORY: 'CLOSE_PROPERTY_HISTORY',
  OPEN_CREATE_PROPERTY_PANEL: 'OPEN_CREATE_PROPERTY_PANEL',
  OPEN_PROPERTY_FILE_DETAILS_PANEL: 'OPEN_PROPERTY_FILE_DETAILS_PANEL',
  OPEN_DATA_AGENT_PANEL: 'OPEN_DATA_AGENT_PANEL',
  OPEN_ENRICHMENT_PROPERTIES_PANEL: 'OPEN_ENRICHMENT_PROPERTIES_PANEL',
  CLOSE_ENRICHMENT_PROPERTIES_PANEL: 'CLOSE_ENRICHMENT_PROPERTIES_PANEL',
  OPEN_SMART_PROPERTIES_PANEL: 'OPEN_SMART_PROPERTIES_PANEL',
  CLOSE_SMART_PROPERTIES_PANEL: 'CLOSE_SMART_PROPERTIES_PANEL',
  POLL_SMART_PROPERTIES: 'POLL_SMART_PROPERTIES',
  OPEN_CONTACT_SOURCING_PANEL: 'OPEN_CONTACT_SOURCING_PANEL',
  CLOSE_CONTACT_SOURCING_PANEL: 'CLOSE_CONTACT_SOURCING_PANEL',
  OPEN_INTENT_SIGNALS_TRACKING: 'OPEN_INTENT_SIGNALS_TRACKING',
  OPEN_INTENT_SIGNALS_SIDEBAR: 'OPEN_INTENT_SIGNALS_SIDEBAR',
  OPEN_CARD_USER_CUSTOMIZATION: 'OPEN_CARD_USER_CUSTOMIZATION',
  OPEN_DEAL_SCORE_HISTORY: 'OPEN_DEAL_SCORE_HISTORY',
  UPDATE_CARD_CONFIGURATION: 'UPDATE_CARD_CONFIGURATION',
  OPEN_COLLAB_SIDEBAR: 'OPEN_COLLAB_SIDEBAR',
  UPDATE_APPROVAL_CARD: 'UPDATE_APPROVAL_CARD',
  UPDATE_RECORD_ATTACHMENTS: 'UPDATE_RECORD_ATTACHMENTS',
  OPEN_ATTACHMENTS_MODAL: 'OPEN_ATTACHMENTS_MODAL',
  OPEN_ATTACHMENTS_FILE_MANAGER: 'OPEN_ATTACHMENTS_FILE_MANAGER',
  OPEN_ATTACHMENTS_REMOVE_MODAL: 'OPEN_ATTACHMENTS_REMOVE_MODAL',
  RECORD_ATTACHMENTS_REMOVED: 'RECORD_ATTACHMENTS_REMOVED',
  OPEN_MANAGE_RECORD_ACCESS_PANEL: 'OPEN_MANAGE_RECORD_ACCESS_PANEL',
  CLOSE_MANAGE_RECORD_ACCESS_PANEL: 'CLOSE_MANAGE_RECORD_ACCESS_PANEL',
  OPEN_SHARE_ENGAGEMENT_ACCESS_MODAL: 'OPEN_SHARE_ENGAGEMENT_ACCESS_MODAL',
  OPEN_ACTIVITY_TIMELINE_SIDEBAR: 'OPEN_ACTIVITY_TIMELINE_SIDEBAR',
  CLOSE_ACTIVITY_TIMELINE_SIDEBAR: 'CLOSE_ACTIVITY_TIMELINE_SIDEBAR',
  OPEN_DEAL_SPLITS_SIDEBAR: 'OPEN_DEAL_SPLITS_SIDEBAR',
  OPEN_DEAL_SPLITS_DELETE_MODAL: 'OPEN_DEAL_SPLITS_DELETE_MODAL',
  DEAL_SPLIT_DELETED: 'DEAL_SPLIT_DELETED',
  OPEN_DEAL_COLLABORATORS_DELETE_MODAL: 'OPEN_DEAL_COLLABORATORS_DELETE_MODAL',
  DEAL_COLLABORATORS_DELETED: 'DEAL_COLLABORATORS_DELETED',
  OPEN_SCORE_HISTORY: 'OPEN_SCORE_HISTORY',
  OPEN_RECENT_PRODUCTS: 'OPEN_RECENT_PRODUCTS',
  OPEN_DEAL_REGISTRATION_SUMMARY: 'OPEN_DEAL_REGISTRATION_SUMMARY',
  CLOSE_DEAL_REGISTRATION_SUMMARY: 'CLOSE_DEAL_REGISTRATION_SUMMARY',
  OPEN_WORKFLOW_ENROLLMENT_MODAL: 'OPEN_WORKFLOW_ENROLLMENT_MODAL',
  UPDATE_WORKFLOW_ENROLLMENTS: 'UPDATE_WORKFLOW_ENROLLMENTS',
  OPEN_MULTI_ACCOUNT_PANEL: 'OPEN_MULTI_ACCOUNT_PANEL',
  OPEN_PORTAL_USERS_PANEL: 'OPEN_PORTAL_USERS_PANEL',
  OPEN_DELETE_ENGAGEMENT_MODAL: 'OPEN_DELETE_ENGAGEMENT_MODAL',
  OPEN_DELETE_SCHEDULED_EMAIL_MODAL: 'OPEN_DELETE_SCHEDULED_EMAIL_MODAL',
  OPEN_DELETE_DRAFT_EMAIL_MODAL: 'OPEN_DELETE_DRAFT_EMAIL_MODAL',
  OPEN_DELETE_TASK_MODAL: 'OPEN_DELETE_TASK_MODAL',
  OPEN_TASK_QUEUE: 'OPEN_TASK_QUEUE',
  FOCUS_TASK_IN_ACTIVE_TASK_QUEUE: 'FOCUS_TASK_IN_ACTIVE_TASK_QUEUE',
  OPEN_REPLACE_PINNED_ENGAGEMENT_MODAL: 'OPEN_REPLACE_PINNED_ENGAGEMENT_MODAL',
  OPEN_MERGE_OBJECTS_MODAL: 'OPEN_MERGE_OBJECTS_MODAL',
  OBJECTS_MERGED: 'OBJECTS_MERGED',
  OPEN_UNMERGE_OBJECTS_MODAL: 'OPEN_UNMERGE_OBJECTS_MODAL',
  OPEN_CLONE_OBJECTS_MODAL: 'OPEN_CLONE_OBJECTS_MODAL',
  OPEN_REVIEW_ASSOCIATIONS_MODAL: 'OPEN_REVIEW_ASSOCIATIONS_MODAL',
  COMMAND_HANDLER_PING: 'COMMAND_HANDLER_PING',
  COMMAND_HANDLER_PONG: 'COMMAND_HANDLER_PONG',
  OPEN_SEQUENCE_ENROLLMENT_MODAL: 'OPEN_SEQUENCE_ENROLLMENT_MODAL',
  OPEN_HIDE_EMAIL_TRACKING_MODAL: 'OPEN_HIDE_EMAIL_TRACKING_MODAL',
  PROPERTY_DEFINITIONS_UPDATED: 'PROPERTY_DEFINITIONS_UPDATED',
  PROPERTY_CREATED_FOR_CARD: 'PROPERTY_CREATED_FOR_CARD',
  INDEX_VIEW_CREATED: 'INDEX_VIEW_CREATED',
  OPEN_ALL_PROPERTIES_PANEL: 'OPEN_ALL_PROPERTIES_PANEL',
  CLOSE_ALL_PROPERTIES_PANEL: 'CLOSE_ALL_PROPERTIES_PANEL',
  OPEN_EMAIL_TRACKING_CONSENT_PANEL: 'OPEN_EMAIL_TRACKING_CONSENT_PANEL',
  CLOSE_EMAIL_TRACKING_CONSENT_PANEL: 'CLOSE_EMAIL_TRACKING_CONSENT_PANEL',
  OPEN_NEXT_STEPS_PANEL: 'OPEN_NEXT_STEPS_PANEL',
  OPEN_ASSOCIATION_HISTORY_PANEL: 'OPEN_ASSOCIATION_HISTORY_PANEL',
  CLOSE_ASSOCIATION_HISTORY_PANEL: 'CLOSE_ASSOCIATION_HISTORY_PANEL',
  OPEN_INFLUENCED_CAMPAIGNS_PANEL: 'OPEN_INFLUENCED_CAMPAIGNS_PANEL',
  OPEN_EDIT_CAMPAIGN_ENGAGEMENT_MODAL: 'OPEN_EDIT_CAMPAIGN_ENGAGEMENT_MODAL',
  OPEN_EDIT_CAMPAIGN_MARKETING_EVENT_MODAL:
    'OPEN_EDIT_CAMPAIGN_MARKETING_EVENT_MODAL',
  OPEN_EDIT_CAMPAIGN_ASSET_MODAL: 'OPEN_EDIT_CAMPAIGN_ASSET_MODAL',
  CREATE_COLUMN_INSIGHTS: 'CREATE_COLUMN_INSIGHTS',
  CRM_OBJECT_TABLE_ADD_COLUMNS: 'CRM_OBJECT_TABLE_ADD_COLUMNS',
  OPEN_CONTRACT_TERMINATE_MODAL: 'OPEN_CONTRACT_TERMINATE_MODAL',
  OPEN_CONTRACT_MANAGE_TERMINATION_MODAL:
    'OPEN_CONTRACT_MANAGE_TERMINATION_MODAL',
  OPEN_CONTRACT_CANCEL_TERMINATION_MODAL:
    'OPEN_CONTRACT_CANCEL_TERMINATION_MODAL',
  OPEN_CONTRACT_CHANGE_QUOTE_MODAL: 'OPEN_CONTRACT_CHANGE_QUOTE_MODAL',
  OPEN_CONTRACT_CANCEL_CHANGE_QUOTE_MODAL:
    'OPEN_CONTRACT_CANCEL_CHANGE_QUOTE_MODAL',
  OPEN_CONTRACT_UPDATE_PAYMENT_METHOD_MODAL:
    'OPEN_CONTRACT_UPDATE_PAYMENT_METHOD_MODAL',
  OPEN_CONTRACT_RENEWAL_QUOTE_MODAL: 'OPEN_CONTRACT_RENEWAL_QUOTE_MODAL',
  OPEN_CONTRACT_CANCEL_RENEWAL_QUOTE_MODAL:
    'OPEN_CONTRACT_CANCEL_RENEWAL_QUOTE_MODAL',
  OPEN_CONTRACT_DELETE_MODAL: 'OPEN_CONTRACT_DELETE_MODAL',
  OPEN_CONTRACT_EDIT_MODAL: 'OPEN_CONTRACT_EDIT_MODAL',
  OPEN_CONTRACT_RENEW_MODAL: 'OPEN_CONTRACT_RENEW_MODAL',
  OPEN_CONTRACT_PAUSE_MODAL: 'OPEN_CONTRACT_PAUSE_MODAL',
  OPEN_CONTRACT_RESUME_MODAL: 'OPEN_CONTRACT_RESUME_MODAL',
  CONTRACT_ACTION_COMPLETED: 'CONTRACT_ACTION_COMPLETED',
  OPEN_CREATE_DEAL_PLAN_MODAL: 'OPEN_CREATE_DEAL_PLAN_MODAL',
  DEAL_PLAN_CREATED: 'DEAL_PLAN_CREATED',
  OPEN_DELETE_DEAL_PLAN_MODAL: 'OPEN_DELETE_DEAL_PLAN_MODAL',
  DEAL_PLAN_DELETED: 'DEAL_PLAN_DELETED',
  OPEN_INVOICE_MODAL: 'OPEN_INVOICE_MODAL',
  OPEN_INVOICE_ACTION_MODAL: 'OPEN_INVOICE_ACTION_MODAL',
  OPEN_INVOICE_SEND_EMAIL_MODAL: 'OPEN_INVOICE_SEND_EMAIL_MODAL',
  OPEN_CREDIT_MEMO_MODAL: 'OPEN_CREDIT_MEMO_MODAL',
  OPEN_EXISTING_SEQUENCE_ENROLLMENT_MODAL:
    'OPEN_EXISTING_SEQUENCE_ENROLLMENT_MODAL',
  OPEN_SEQUENCE_UNENROLL_MODAL: 'OPEN_SEQUENCE_UNENROLL_MODAL',
  OPEN_BILLING_WIZARD_MODAL: 'OPEN_BILLING_WIZARD_MODAL',
  OPEN_SUBSCRIPTIONS_ADMIN_MODAL: 'OPEN_SUBSCRIPTIONS_ADMIN_MODAL',
  OPEN_PROSPECTING_AGENT_MODAL: 'OPEN_PROSPECTING_AGENT_MODAL',
  OPEN_ROUTING_ASSIGNMENT_DETAILS_PANEL:
    'OPEN_ROUTING_ASSIGNMENT_DETAILS_PANEL',
  PROSPECTING_AGENT_ENROLLMENT: 'PROSPECTING_AGENT_ENROLLMENT',
  PROSPECTING_AGENT_UNENROLLMENT: 'PROSPECTING_AGENT_UNENROLLMENT',
  OPEN_CALL_TRANSCRIPT_SIDEBAR: 'OPEN_CALL_TRANSCRIPT_SIDEBAR',
  CLOSE_CALL_TRANSCRIPT_SIDEBAR: 'CLOSE_CALL_TRANSCRIPT_SIDEBAR',
  GOOGLE_DRIVE_INTEGRATION: 'GOOGLE_DRIVE_INTEGRATION',
  OPEN_SMART_DATA_CAPTURE_MODAL: 'OPEN_SMART_DATA_CAPTURE_MODAL',
  UPDATE_SMART_DATA_PROPERTY_SUGGESTIONS:
    'UPDATE_SMART_DATA_PROPERTY_SUGGESTIONS',
  CARD_CONFIG_CHANGED: 'CARD_CONFIG_CHANGED',
  CARD_ORDER_CHANGED: 'CARD_ORDER_CHANGED',
  CLOSE_PREVIEW_ENGAGEMENT: 'CLOSE_PREVIEW_ENGAGEMENT',
  OPEN_BUYING_GROUP_CANVAS_MODAL: 'OPEN_BUYING_GROUP_CANVAS_MODAL',
  BUYING_GROUP_CANVAS_MODAL_CLOSED: 'BUYING_GROUP_CANVAS_MODAL_CLOSED',
  OPEN_ACCOUNT_MAP_CANVAS_MODAL: 'OPEN_ACCOUNT_MAP_CANVAS_MODAL',
  ACCOUNT_MAP_CANVAS_UPDATED: 'ACCOUNT_MAP_CANVAS_UPDATED',
  OPEN_IMPORT_CONTACTS_MODAL: 'OPEN_IMPORT_CONTACTS_MODAL',
  OPEN_MARKETING_EVENT_CONTACTS_MODAL: 'OPEN_MARKETING_EVENT_CONTACTS_MODAL',
  OPEN_ADD_POI_MODAL: 'OPEN_ADD_POI_MODAL',
  OPEN_SALES_ORDER_WIZARD: 'OPEN_SALES_ORDER_WIZARD',
  OPEN_QUALIFIED_LEADS_HISTORY_PANEL: 'OPEN_QUALIFIED_LEADS_HISTORY_PANEL',
  OPEN_RAISE_A_LEAD_PANEL: 'OPEN_RAISE_A_LEAD_PANEL',
  VIEW_UPDATE_REQUESTED: 'VIEW_UPDATE_REQUESTED',
  VIEW_UPDATE_COMPLETED: 'VIEW_UPDATE_COMPLETED',
  VIEW_WORKING_LAYER_UPDATED: 'VIEW_WORKING_LAYER_UPDATED',
  VIEW_WORKING_LAYER_RESET: 'VIEW_WORKING_LAYER_RESET',
  OPEN_CUSTOM_ENGAGEMENT_PROPERTY_SETTINGS:
    'OPEN_CUSTOM_ENGAGEMENT_PROPERTY_SETTINGS',
  OPEN_RESTORE_REQUEST_SIDE_PANEL: 'OPEN_RESTORE_REQUEST_SIDE_PANEL',
  OPEN_RECENT_CONVERSION_HISTORY_PANEL: 'OPEN_RECENT_CONVERSION_HISTORY_PANEL',
  OPEN_DEAL_SUPPORT_REQUEST_PANEL: 'OPEN_DEAL_SUPPORT_REQUEST_PANEL',
  OPEN_SALES_MANAGER_ESCALATION_PANEL: 'OPEN_SALES_MANAGER_ESCALATION_PANEL',
  DEAL_SUPPORT_REQUEST_SUBMITTED: 'DEAL_SUPPORT_REQUEST_SUBMITTED',
  SALES_MANAGER_ESCALATION_SUBMITTED: 'SALES_MANAGER_ESCALATION_SUBMITTED',
  UPDATE_EMAIL_COMPOSER_CONTENT: 'UPDATE_EMAIL_COMPOSER_CONTENT',
  OPEN_UNFOLLOW_MODAL: 'OPEN_UNFOLLOW_MODAL',
  OPEN_MANAGE_OWNERSHIP_MODAL: 'OPEN_MANAGE_OWNERSHIP_MODAL',
  OPEN_REMOVE_AND_RECYCLE_PANEL: 'OPEN_REMOVE_AND_RECYCLE_PANEL',
  OPEN_REJECT_LEAD_MODAL: 'OPEN_REJECT_LEAD_MODAL',
  OPEN_DISPUTE_DOMAIN_MODAL: 'OPEN_DISPUTE_DOMAIN_MODAL',
  OPEN_ENRICH_DOMAIN_MODAL: 'OPEN_ENRICH_DOMAIN_MODAL',
  OPEN_CHANGE_DOMAIN_URL_MODAL: 'OPEN_CHANGE_DOMAIN_URL_MODAL',
  OPEN_MOVE_CONTACT_MODAL: 'OPEN_MOVE_CONTACT_MODAL',
  OPEN_REMOVE_BAD_FIT_MODAL: 'OPEN_REMOVE_BAD_FIT_MODAL',
  OPEN_DOMAIN_HISTORY_PANEL: 'OPEN_DOMAIN_HISTORY_PANEL',
  DOMAIN_CONTROLS_ACTION_COMPLETED: 'DOMAIN_CONTROLS_ACTION_COMPLETED',
  DOMAIN_CONTROLS_MODAL_CLOSED: 'DOMAIN_CONTROLS_MODAL_CLOSED',
  OPEN_QUOTE_EDITOR: 'OPEN_QUOTE_EDITOR',
  OPEN_CPQ_QUOTE_SIDEBAR: 'OPEN_CPQ_QUOTE_SIDEBAR',
  OPEN_PROSPECTING_OUTREACH: 'OPEN_PROSPECTING_OUTREACH',
  CLOSE_PROSPECTING_OUTREACH: 'CLOSE_PROSPECTING_OUTREACH',
  PROSPECTING_OUTREACH_STATE_CHANGED: 'PROSPECTING_OUTREACH_STATE_CHANGED',
  QUOTE_EDITOR_CLOSED: 'QUOTE_EDITOR_CLOSED',
  OPEN_PRODUCT_EDITOR: 'OPEN_PRODUCT_EDITOR',
  OPEN_PRICE_BOOK_AUTO_ASSIGNMENT_MODAL:
    'OPEN_PRICE_BOOK_AUTO_ASSIGNMENT_MODAL',
  OPEN_CREATE_PAYMENT_PLAN: 'OPEN_CREATE_PAYMENT_PLAN',
  OPEN_EDIT_PAYMENT_PLAN: 'OPEN_EDIT_PAYMENT_PLAN',
  OPEN_CANCEL_PAYMENT_PLAN: 'OPEN_CANCEL_PAYMENT_PLAN',
  OPEN_PAYMENT_LINK_IFRAME: 'OPEN_PAYMENT_LINK_IFRAME',
  OPEN_FLYWHEEL_COLD_SOURCE_MODAL: 'OPEN_FLYWHEEL_COLD_SOURCE_MODAL',
  OPEN_PAYMENT_LINK_ASSOCIATION_PANEL: 'OPEN_PAYMENT_LINK_ASSOCIATION_PANEL',
  OPEN_PAYMENT_LINK_CREATE_MODAL: 'OPEN_PAYMENT_LINK_CREATE_MODAL',
  PAYMENT_PLAN_UPDATED: 'PAYMENT_PLAN_UPDATED',
  OPEN_NBA_RECOMMENDATIONS_SIDEBAR: 'OPEN_NBA_RECOMMENDATIONS_SIDEBAR',
  SELECT_CONTAINER_TAB: 'SELECT_CONTAINER_TAB',
  EXPAND_RIGHT_SIDEBAR: 'EXPAND_RIGHT_SIDEBAR',
  RIGHT_SIDEBAR_EXPANDED: 'RIGHT_SIDEBAR_EXPANDED',
  EXPAND_COLLAPSIBLE_CONTAINER: 'EXPAND_COLLAPSIBLE_CONTAINER',
  EXPAND_COLLAPSIBLE_CARD: 'EXPAND_COLLAPSIBLE_CARD',
} as const;

export type TopicName = keyof typeof TOPIC_NAMES;
